import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { setCrownHidden, setPause, updateLanguage } from '../lib/api'
import { setLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n'
import { useUserStore } from '../store/userStore'

interface SettingsSheetProps {
  onClose: () => void
}

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'settings.english',
  id: 'settings.indonesian',
}

export default function SettingsSheet({ onClose }: SettingsSheetProps) {
  const { t, i18n } = useTranslation()
  const {
    me,
    setPaused,
    setCrownHidden: setCrownHiddenLocal,
  } = useUserStore()

  function handlePick(code: SupportedLanguage) {
    if (code === i18n.language) return
    setLanguage(code)
    // Best-effort sync to the backend — also drives bot replies/notifications.
    // A failed save never blocks the local language switch.
    updateLanguage(code).catch(() => {})
  }

  function handleTogglePause() {
    if (!me) return
    const next = !me.is_paused
    // Optimistic update so SwipePage reacts immediately; revert on failure.
    setPaused(next)
    setPause(next).catch(() => setPaused(!next))
  }

  function handleToggleCrownHidden() {
    if (!me) return
    const next = !me.crown_hidden
    // Optimistic update, same pattern as handleTogglePause; revert on failure.
    setCrownHiddenLocal(next)
    setCrownHidden(next).catch(() => setCrownHiddenLocal(!next))
  }

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col justify-end bg-black/50"
      onClick={onClose}
    >
      <motion.div
        className="max-h-[85dvh] overflow-y-auto rounded-t-2xl bg-tg-bg"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pb-8 pt-5">
          <p className="mb-5 text-lg font-semibold text-tg-text">{t('settings.title')}</p>

          <div className="mb-5">
            <p className="mb-2 text-sm font-medium text-tg-hint">{t('settings.language')}</p>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_LANGUAGES.map((code) => (
                <button
                  key={code}
                  onClick={() => handlePick(code)}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                    i18n.language === code
                      ? 'bg-primary text-white'
                      : 'bg-tg-secondary-bg text-tg-text'
                  }`}
                >
                  {t(LANGUAGE_LABELS[code])}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-tg-text">{t('settings.pauseTitle')}</p>
                <p className="mt-0.5 text-xs text-tg-hint">{t('settings.pauseDescription')}</p>
              </div>
              <button
                onClick={handleTogglePause}
                aria-pressed={me?.is_paused ?? false}
                className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  me?.is_paused ? 'bg-primary text-white' : 'bg-tg-secondary-bg text-tg-text'
                }`}
              >
                {me?.is_paused ? t('settings.pauseOn') : t('settings.pauseOff')}
              </button>
            </div>
          </div>

          {/* Crown opt-out — only rendered when the caller is actually
              crown-eligible (see GET /me crown_eligible). See
              docs/monetization-roadmap.md "Kejar Premium Feature" #1. */}
          {me?.crown_eligible && (
            <div className="mb-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-tg-text">{t('settings.crownTitle')}</p>
                  <p className="mt-0.5 text-xs text-tg-hint">{t('settings.crownDescription')}</p>
                </div>
                <button
                  onClick={handleToggleCrownHidden}
                  aria-pressed={!me.crown_hidden}
                  className={`shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                    !me.crown_hidden ? 'bg-primary text-white' : 'bg-tg-secondary-bg text-tg-text'
                  }`}
                >
                  {me.crown_hidden ? t('settings.crownOff') : t('settings.crownOn')}
                </button>
              </div>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full rounded-xl bg-tg-secondary-bg py-3 text-sm font-medium text-tg-text active:opacity-70"
          >
            {t('common.close')}
          </button>
        </div>
      </motion.div>
    </div>
  )
}
